import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdArrowBackIos } from "react-icons/md";
import {
    UsdIbanSection,
    UsdSwiftSection,
    UsdAchSection,
    UsdRoutingNumberField,
} from "./RecipientUtils";
import Input from "component/form/Input";
import useUsdRoutingVerification from "hooks/useUsdRoutingVerification";

import {
    patchCrossBorderTransfer,
    selectCrossBorderTransfer,
    setCrossBorderRecipientMeta,
    setCrossBorderUsdAll,
} from "../../redux/slices/crossBorderTransferSlice";

const RecipientToAccount = {
    US: "us",
    NON_US: "iban",
    INTERNATIONAL_WIRE: "international_wire",
};

const AccountToRecipient = {
    us: "US",
    iban: "NON_US",
    swift: "INTERNATIONAL_WIRE",
    international_wire: "INTERNATIONAL_WIRE",
};

const COUNTRY_OPTIONS = [
    { value: "DE", label: "Germany" },
    { value: "GB", label: "United Kingdom" },
    { value: "FR", label: "France" },
    { value: "NL", label: "Netherlands" },
    { value: "IE", label: "Ireland" },
];

const ACCOUNT_TYPE_OPTIONS = [
    { value: "checking", label: "Checking" },
    { value: "savings", label: "Savings" },
];

const getCountryForRecipientType = (country, accountType) => {
    if (accountType === "iban") {
        return country === "US" ? "" : country || "";
    }

    return country || "US";
};

export function CrossBorderRecipientFormUSD({ onBack, nextRoute }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const cb = useSelector(selectCrossBorderTransfer);
    const businessId = useSelector((state) => state.business?._id);

    const usd = cb?.recipient?.usd || {};

    const [recipientType] = useState(() => {
        const saved = sessionStorage.getItem("recipientType");
        if (saved && ["US", "NON_US", "INTERNATIONAL_WIRE"].includes(saved)) return saved;
        const fromState = usd?.account_type ? AccountToRecipient[usd.account_type] : null;
        return fromState || "US";
    });

    const selectedPaymentRail =
        usd?.paymentRail || sessionStorage.getItem("paymentRail") || "ach";
    const selectedAccountType = RecipientToAccount[recipientType] || "us";
    const isSelectedSwift =
        recipientType === "INTERNATIONAL_WIRE" ||
        (recipientType === "US" && selectedPaymentRail === "swift") ||
        usd?.account_type === "international_wire";
    const accountTypeForApi = isSelectedSwift ? "international_wire" : selectedAccountType;
    const isIBANFlow = accountTypeForApi === "iban";
    const isSwiftFlow = accountTypeForApi === "international_wire";

    const bankNameInStore = cb?.recipient?.bankName || usd?.bank_name || "";
    const [bankName, setBankName] = useState(bankNameInStore);

    const [holderType, setHolderType] = useState(usd?.account_owner_type || "");
    const [accountName, setAccountName] = useState(usd?.account_owner_name || "");

    // identity
    const [firstName, setFirstName] = useState(usd?.first_name || "");
    const [lastName, setLastName] = useState(usd?.last_name || "");
    const [businessName, setBusinessName] = useState(usd?.business_name || "");

    // ACH / account block
    const [accountType, setAccountType] = useState(usd?.account?.checking_or_savings || "");
    const [routingNumber, setRoutingNumber] = useState(usd?.account?.routing_number || "");
    const [accountNumber, setAccountNumber] = useState(
        usd?.account?.account_number || usd?.swift?.account_number || ""
    );

    // IBAN/SWIFT fields
    const [bankCountryCode, setBankCountryCode] = useState(() =>
        getCountryForRecipientType(
            usd?.iban?.country || usd?.swift?.country || usd?.address?.country,
            accountTypeForApi
        )
    );
    const [iban, setIban] = useState(usd?.iban?.account_number || "");
    const [bic, setBic] = useState(usd?.iban?.bic || usd?.swift?.bic || "");

    // Address
    const [recStreet, setRecStreet] = useState(usd?.address?.street_line_1 || "");
    const [recCountry, setRecCountry] = useState(() =>
        getCountryForRecipientType(usd?.address?.country, accountTypeForApi)
    );
    const [recCity, setRecCity] = useState(usd?.address?.city || "");
    const [recState, setRecState] = useState(usd?.address?.state || "");
    const [recZip, setRecZip] = useState(usd?.address?.postal_code || "");
    const [bankStreet, setBankStreet] = useState(usd?.bank_address?.street_line_1 || "");
    const [bankCountry, setBankCountry] = useState(usd?.bank_address?.country || "US");
    const [bankCity, setBankCity] = useState(usd?.bank_address?.city || "");
    const [bankState, setBankState] = useState(usd?.bank_address?.state || "");
    const [bankZip, setBankZip] = useState(usd?.bank_address?.postal_code || "");
    const [relationship, setRelationship] = useState(usd?.relationship || "");
    const [recipientEmail, setRecipientEmail] = useState(usd?.email || "");
    const {
        details: verifiedRoutingDetails,
        error: routingVerificationError,
        loading: routingVerificationLoading,
    } = useUsdRoutingVerification({
        businessId,
        currency: String(cb?.fromCurrency || "").toUpperCase(),
        currencyTo: String(cb?.recipient?.toCurrency || "USD").toUpperCase(),
        enabled: !isIBANFlow,
        routingNumber,
    });

    const recipientLocationLabel = isIBANFlow
        ? "Recipient outside the US"
        : "Recipient in the US";
    const paymentMethodLabel = isIBANFlow
        ? "IBAN / SWIFT"
        : isSwiftFlow
          ? "International wire transfer"
          : "ACH";

    useEffect(() => {
        if (!verifiedRoutingDetails) return;

        if (verifiedRoutingDetails.accountName) {
            setAccountName(verifiedRoutingDetails.accountName);
        }
        if (verifiedRoutingDetails.bankName) setBankName(verifiedRoutingDetails.bankName);
        if (verifiedRoutingDetails.bic) setBic(verifiedRoutingDetails.bic);
        if (verifiedRoutingDetails.bankStreet) setBankStreet(verifiedRoutingDetails.bankStreet);
        if (verifiedRoutingDetails.bankCity) setBankCity(verifiedRoutingDetails.bankCity);
        if (verifiedRoutingDetails.bankState) setBankState(verifiedRoutingDetails.bankState);
        if (verifiedRoutingDetails.bankZip) setBankZip(verifiedRoutingDetails.bankZip);
        if (verifiedRoutingDetails.bankCountry) {
            const country = verifiedRoutingDetails.bankCountry;
            const normalizedCountry = country.length === 2 ? country.toUpperCase() : "US";
            setBankCountry(normalizedCountry);
            setBankCountryCode(normalizedCountry);
        }
    }, [verifiedRoutingDetails]);

    useEffect(() => {
        const u = cb?.recipient?.usd || {};
        setHolderType(u?.account_owner_type || "");
        setAccountName(u?.account_owner_name || "");
        setFirstName(u?.first_name || "");
        setLastName(u?.last_name || "");
        setBusinessName(u?.business_name || "");

        setAccountType(u?.account?.checking_or_savings || "");
        const storeRouting =
            accountTypeForApi === "us"
                ? u?.account?.routing_number || ""
                : u?.swift?.routing_number || "";

        setRoutingNumber((prev) => (prev ? prev : storeRouting));

        setAccountNumber(u?.account?.account_number || u?.swift?.account_number || "");

        setBankCountryCode(
            getCountryForRecipientType(
                u?.iban?.country || u?.swift?.country || u?.address?.country,
                accountTypeForApi
            )
        );
        setIban(u?.iban?.account_number || "");
        setBic(u?.iban?.bic || u?.swift?.bic || "");

        setRecStreet(u?.address?.street_line_1 || "");
        setRecCountry(getCountryForRecipientType(u?.address?.country, accountTypeForApi));
        setRecCity(u?.address?.city || "");
        setRecState(u?.address?.state || "");
        setRecZip(u?.address?.postal_code || "");
        setBankStreet(u?.bank_address?.street_line_1 || "");
        setBankCountry(u?.bank_address?.country || "US");
        setBankCity(u?.bank_address?.city || "");
        setBankState(u?.bank_address?.state || "");
        setBankZip(u?.bank_address?.postal_code || "");
        setRelationship(u?.relationship || "");
        setRecipientEmail(u?.email || "");

        // bank name
        const bn = cb?.recipient?.bankName || u?.bank_name || "";
        setBankName((prev) => (prev ? prev : bn));
    }, [cb?.recipient?.usd, cb?.recipient?.bankName, accountTypeForApi]);

    // Persist method (recipientType) to session + slice
    useEffect(() => {
        sessionStorage.setItem("recipientType", recipientType);
        dispatch(setCrossBorderUsdAll({ account_type: accountTypeForApi }));
    }, [recipientType, accountTypeForApi, dispatch]);

    // Compose owner name
    const accountOwnerName = useMemo(() => (accountName || "").trim(), [accountName]);

    useEffect(() => {
        const bank_name = bankName || "";

        const recipientBase = {
            bank_name,
            account_type: accountTypeForApi,
            paymentRail: selectedPaymentRail,
            location: accountTypeForApi === "us" ? "US" : "nonUS",
            account_owner_type: holderType || "",
            first_name: holderType === "individual" ? firstName : "",
            last_name: holderType === "individual" ? lastName : "",
            business_name: holderType === "corporate" ? businessName : "",
            account_owner_name: accountOwnerName,
            address: {
                street_line_1: recStreet || "",
                city: recCity || "",
                state: recState || "",
                postal_code: recZip || "",
                country: recCountry || "",
            },
            relationship: relationship || "",
            email: recipientEmail || "",
            ...(!isIBANFlow && !isSwiftFlow
                ? {
                      bank_address: {
                          street_line_1: bankStreet || "",
                          city: bankCity || "",
                          state: bankState || "",
                          postal_code: bankZip || "",
                          country: bankCountry || "US",
                      },
                  }
                : {}),
        };

        let methodBlock = {};
        if (accountTypeForApi === "iban") {
            methodBlock = {
                iban: {
                    account_number: iban || "",
                    bic: bic || "",
                    country: bankCountryCode || "",
                },
            };
        } else if (accountTypeForApi === "international_wire") {
            methodBlock = {
                swift: {
                    account_number: accountNumber || "",
                    routing_number: routingNumber || "",
                    bic: bic || "",
                    country: bankCountryCode || "",
                },
            };
        } else {
            methodBlock = {
                account: {
                    account_number: accountNumber || "",
                    routing_number: routingNumber || "",
                    checking_or_savings: accountType || "",
                },
            };
        }

        dispatch(setCrossBorderUsdAll({ ...recipientBase, ...methodBlock }));
        dispatch(
            setCrossBorderRecipientMeta({
                bankName: bank_name,
                country: recipientBase.address.country,
            })
        );
    }, [
        dispatch,
        bankName,
        accountTypeForApi,
        selectedPaymentRail,
        holderType,
        firstName,
        lastName,
        businessName,
        accountOwnerName,
        recStreet,
        recCountry,
        recCity,
        recState,
        recZip,
        bankStreet,
        bankCountry,
        bankCity,
        bankState,
        bankZip,
        relationship,
        recipientEmail,
        bankCountryCode,
        iban,
        bic,
        accountNumber,
        routingNumber,
        accountType,
        isIBANFlow,
        isSwiftFlow,
    ]);

    const holderOk =
        holderType === "individual"
            ? !!firstName && !!lastName
            : holderType === "corporate"
              ? !!businessName
              : false;

    const bankOk = !!(bankName || "").trim();

    const canContinueUSD =
        !isIBANFlow &&
        !isSwiftFlow &&
        !!holderType &&
        holderOk &&
        !!accountType &&
        !!routingNumber &&
        !!accountNumber &&
        !!accountName &&
        bankOk &&
        !!recStreet &&
        !!recCountry &&
        !!recCity &&
        !!recState &&
        !!recZip &&
        !!bankStreet &&
        !!bankCountry &&
        !!bankCity &&
        !!bankState &&
        !!bankZip &&
        !!relationship &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail);

    const canContinueIBAN =
        isIBANFlow &&
        !!holderType &&
        holderOk &&
        !!bankCountryCode &&
        !!iban &&
        !!bic &&
        !!accountName &&
        bankOk &&
        !!relationship &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail) &&
        !!recStreet &&
        !!recCountry &&
        !!recCity &&
        !!recState &&
        !!recZip;

    const canContinueSWIFT =
        isSwiftFlow &&
        !!holderType &&
        holderOk &&
        !!bankCountryCode &&
        !!accountNumber &&
        !!bic &&
        bankOk &&
        !!recStreet &&
        !!recCity &&
        !!recState &&
        !!recZip;

    const canContinue = isIBANFlow
        ? canContinueIBAN
        : isSwiftFlow
          ? canContinueSWIFT
          : canContinueUSD;

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-medium text-[#101828]">Add recipient</h1>

            <Input
                disabled
                required={false}
                label="Recipient location"
                labelClassName="!mt-0 text-[#101828]"
                value={recipientLocationLabel}
                className="!mt-0"
            />
            <Input
                disabled
                required={false}
                label="Payment method"
                labelClassName="!mt-0 text-[#101828]"
                value={paymentMethodLabel}
                className="!mt-0"
            />
            {!isIBANFlow && (
                <UsdRoutingNumberField
                    error={routingVerificationError}
                    loading={routingVerificationLoading}
                    routingNumber={routingNumber}
                    setRoutingNumber={setRoutingNumber}
                />
            )}

            {isIBANFlow ? (
                <UsdIbanSection
                    bankCountryCode={bankCountryCode}
                    setBankCountryCode={setBankCountryCode}
                    bankName={bankName}
                    setBankName={setBankName}
                    accountName={accountName}
                    setAccountName={setAccountName}
                    holderType={holderType}
                    setHolderType={setHolderType}
                    firstName={firstName}
                    setFirstName={setFirstName}
                    lastName={lastName}
                    setLastName={setLastName}
                    businessName={businessName}
                    setBusinessName={setBusinessName}
                    recStreet={recStreet}
                    setRecStreet={setRecStreet}
                    recCountry={recCountry}
                    setRecCountry={setRecCountry}
                    recCity={recCity}
                    setRecCity={setRecCity}
                    recState={recState}
                    setRecState={setRecState}
                    recZip={recZip}
                    setRecZip={setRecZip}
                    iban={iban}
                    setIban={setIban}
                    bic={bic}
                    setBic={setBic}
                    relationship={relationship}
                    setRelationship={setRelationship}
                    recipientEmail={recipientEmail}
                    setRecipientEmail={setRecipientEmail}
                />
            ) : isSwiftFlow ? (
                <UsdSwiftSection
                    COUNTRY_OPTIONS={COUNTRY_OPTIONS}
                    bankCountryCode={bankCountryCode}
                    setBankCountryCode={setBankCountryCode}
                    selectedBank={{ name: bankName }}
                    onOpenBankModal={() => {}}
                    bankName={bankName}
                    setBankName={setBankName}
                    holderType={holderType}
                    setHolderType={setHolderType}
                    firstName={firstName}
                    setFirstName={setFirstName}
                    lastName={lastName}
                    setLastName={setLastName}
                    businessName={businessName}
                    setBusinessName={setBusinessName}
                    recStreet={recStreet}
                    setRecStreet={setRecStreet}
                    recCity={recCity}
                    setRecCity={setRecCity}
                    recState={recState}
                    setRecState={setRecState}
                    recZip={recZip}
                    setRecZip={setRecZip}
                    accountNumber={accountNumber}
                    setAccountNumber={setAccountNumber}
                    routingNumber={routingNumber}
                    setRoutingNumber={setRoutingNumber}
                    bic={bic}
                    setBic={setBic}
                    verifiedBankDetails={verifiedRoutingDetails}
                />
            ) : (
                <UsdAchSection
                    holderType={holderType}
                    setHolderType={setHolderType}
                    firstName={firstName}
                    setFirstName={setFirstName}
                    lastName={lastName}
                    setLastName={setLastName}
                    businessName={businessName}
                    setBusinessName={setBusinessName}
                    accountType={accountType}
                    setAccountType={setAccountType}
                    ACCOUNT_TYPE_OPTIONS={ACCOUNT_TYPE_OPTIONS}
                    routingNumber={routingNumber}
                    setRoutingNumber={setRoutingNumber}
                    accountNumber={accountNumber}
                    setAccountNumber={setAccountNumber}
                    accountName={accountOwnerName}
                    setAccountName={setAccountName}
                    bankName={bankName}
                    setBankName={setBankName}
                    bankStreet={bankStreet}
                    setBankStreet={setBankStreet}
                    bankCountry={bankCountry}
                    setBankCountry={setBankCountry}
                    bankState={bankState}
                    setBankState={setBankState}
                    bankCity={bankCity}
                    setBankCity={setBankCity}
                    bankZip={bankZip}
                    setBankZip={setBankZip}
                    relationship={relationship}
                    setRelationship={setRelationship}
                    recipientEmail={recipientEmail}
                    setRecipientEmail={setRecipientEmail}
                    recStreet={recStreet}
                    setRecStreet={setRecStreet}
                    recCountry={recCountry}
                    setRecCountry={setRecCountry}
                    recCity={recCity}
                    setRecCity={setRecCity}
                    recState={recState}
                    setRecState={setRecState}
                    recZip={recZip}
                    setRecZip={setRecZip}
                    verifiedBankDetails={verifiedRoutingDetails}
                />
            )}

            <div className="flex justify-between items-center mt-8">
                <button
                    className="text-[#101828] flex items-center gap-2"
                    onClick={() => onBack?.("Beneficiaries")}
                >
                    <MdArrowBackIos /> Select from beneficiaries
                </button>

                <div className="flex gap-8 items-center">
                    <button className="text-[#101828]" onClick={() => navigate(-1)}>
                        Back
                    </button>
                    <button
                        onClick={() => {
                            if (!canContinue) return;
                            dispatch(patchCrossBorderTransfer({ recipientSkipped: false }));
                            navigate(nextRoute);
                        }}
                        disabled={!canContinue}
                        className={`rounded-lg px-6 py-2 font-semibold text-white transition ${
                            canContinue
                                ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                        }`}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}
